import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../mendepartmentdetails/Sidebar';
import SubDeptImage from '../../../assets/image/department/red-ship-s-horn-is-fixed-steel-rack-against-blue-sky_187194-870.jpg';

function PublicAlertsWarnings() {
  const [Faq, setFaq] = useState(0); // first dropdown open by default
  const titleRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');

  useEffect(() => {
    if (titleRef.current) {
      const c = window.getComputedStyle(titleRef.current).color;
      setTitleColor(c);
    }
  }, []);

  // shared inline styles (match structure used on other extended pages)
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.35, color: '#0f172a' };
  const valText = { fontSize: '1rem', lineHeight: 1.6, color: '#334155' };
  const overviewText = { color: titleColor || '#0f172a', fontSize: '1.05rem', lineHeight: 1.6, margin: '8px 0 16px' };

  const sections = [
    {
      Que: 'How Alerts Reach You',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Wireless Emergency Alerts (WEA):', 'Location-based messages to compatible cell phones for imminent threats, AMBER Alerts, and public safety messages. No signup required.'],
            ['Emergency Alert System (EAS):', 'Interrupts TV/radio for urgent, broad-area warnings.'],
            ['NOAA Weather Radio:', '24/7 hazard broadcasts with battery backup; recommended as a redundant alerting method.'],
            ['Outdoor Warning Sirens:', 'Primarily for people outdoors; go inside and check official sources when you hear a siren.'],
          ].map(([k, v], i) => (
            <li key={`reach-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Sign Up for Local Alerts',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Opt-in systems:', 'Counties/cities may offer text/email/voice alerts for local incidents, boil water notices, closures, and evacuations.'],
            ['Profile details:', 'Add home, work, and school addresses; enable push notifications on your phone; include assistive needs if asked.'],
            ['Family sharing:', 'Register additional phone numbers/emails for household members so everyone gets notices.'],
          ].map(([k, v], i) => (
            <li key={`optin-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'What the Alerts Mean',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Advisory:', 'Be aware; conditions may cause inconvenience (e.g., air quality, minor flooding).'],
            ['Watch:', 'Be prepared; conditions are favorable for a hazard (e.g., severe storms, wildfire weather).'],
            ['Warning:', 'Take action now; a hazard is occurring or imminent (e.g., tornado, flash flood, evacuation).'],
            ['Shelter-in-Place / Evacuate:', 'Follow instructions immediately; routes and shelters will be provided when applicable.'],
          ].map(([k, v], i) => (
            <li key={`meaning-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Accessibility & Languages',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Multiple languages:', 'Critical messages may include translated content or links to translated pages.'],
            ['Accessible formats:', 'Use screen-reader compatible devices; enable vibration/flash alerts if available.'],
            ['Community support:', 'Coordinate with neighbors who may need help receiving or acting on alerts.'],
          ].map(([k, v], i) => (
            <li key={`access-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Testing & System Reliability',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Periodic tests:', 'Agencies may issue test messages to verify reach and performance; tests are clearly labeled.'],
            ['Redundancy:', 'Use at least two alerting sources (e.g., WEA + NOAA radio) in case one path fails.'],
            ['Phone settings:', 'Keep emergency alerts enabled; disable “Do Not Disturb” exceptions that block alarms.'],
          ].map(([k, v], i) => (
            <li key={`test-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Best Practices for Households',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Respond immediately:', 'Treat “Warning” messages as actionable; move to safety first, then seek details.'],
            ['Know your zones:', 'Learn flood/evacuation/wildfire zones and routes; keep paper maps as backup.'],
            ['Prepare contacts:', 'Designate an out-of-area contact; teach family how to share location and status.'],
          ].map(([k, v], i) => (
            <li key={`home-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'For Schools, Businesses, and Facilities',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Internal relays:', 'Tie public alerts into PA systems, desktop alerts, and signage for rapid dissemination.'],
            ['Templates & roles:', 'Pre-approved message templates and trained PIOs speed up accurate notifications.'],
            ['Accounting for everyone:', 'Roster checks, shelter areas, and assistance for people with disabilities or visitors.'],
          ].map(([k, v], i) => (
            <li key={`org-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Common Issues & Troubleshooting',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Didn’t receive a WEA:', 'Old phone/OS, carrier coverage, or device settings can block alerts—update software and enable alerts.'],
            ['Missed siren:', 'Sirens are outdoor-only; rely on indoor alerts (phone/NOAA radio) for inside spaces.'],
            ['Conflicting info:', 'Follow official agency channels; avoid sharing unverified social posts during incidents.'],
          ].map(([k, v], i) => (
            <li key={`trouble-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
  ];

  return (
    <section className="department-details-section">
      <div className="container">
        <div className="row">

          {/* Sidebar */}
          <div className="col-12 col-lg-4 col-xl-4">
            <Sidebar />
          </div>

          {/* Main Content */}
          <div className="col-lg-8">
            <div className="department-details-imgbox">
              <img src={SubDeptImage} className="img-fluid" alt="Public Alerts and Warning Systems" />
            </div>

            <div className="department-details-content-box">
              <h4 ref={titleRef} className="department-details-title">Public Alerts and Warning Systems</h4>

              {/* Overview (always visible, same color as title but smaller) */}
              <p style={overviewText}>
                We deliver timely, accurate warnings through multiple channels—phones, broadcasters, weather radio, and sirens—
                so people can act fast. Use at least two alert sources and keep emergency alerts enabled on your mobile devices.
              </p>
            </div>

            {/* Accordion (expanded information) */}
            <div className="accordian-box">
              {sections.map((item, index) => (
                <div className={`accordian-box-item ${Faq !== index && 'active'}`} key={index}>
                  <div className="accordian-title">
                    <h5>{item.Que}</h5>
                    <i
                      className="fa-solid fa-angle-right"
                      onClick={() => setFaq(index)}
                      role="button"
                      aria-label={`Toggle ${item.Que}`}
                    />
                  </div>

                  {Faq === index && (
                    <div className="accordian-content" style={{ display: 'block' }}>
                      {item.Answer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="department-details-policy-box">
              <h4>Our Commitment:</h4>
              <p>
                We provide clear, accessible alerts and maintain redundant systems to reach the public quickly—reducing confusion,
                speeding protective actions, and saving lives.
              </p>
            </div>
            <div>
              <Link to="/department/emergency">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: '5px' }} />
                Go Back
              </Link>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

export default PublicAlertsWarnings;
